import {
  SubscriptionBundlePrice,
  SubscriptionBundle,
  RawSubscriptionBundle,
  RawPrograms,
  RawSubscriptionTier,
  SubscriptionProviderPriceIds,
} from '../../common/bundle.types';
import {
  UpdateBundleInput,
  BundleItemType,
  BillingCycleInterval,
  ProgramBusinessModel,
} from 'generated/graphql';

export const toSubscriptionPrograms = (programs?: RawPrograms) =>
  (programs || [])
    .filter(
      (p) =>
        p.programBusinessModel === ProgramBusinessModel.Subscription ||
        p.programBusinessModel === ProgramBusinessModel.Unassigned,
    )
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      isPublished: !!p.publishedAt && !p.archivedAt,
    }));

export const toSubscriptionBundles = (
  bundles: RawSubscriptionBundle[],
): SubscriptionBundle[] => {
  const mappedBundles = bundles.map((b) => ({
    id: b.id,
    title: b.isPlatformBundle ? 'Platform users' : 'Exclusive Subscription',
    influencerId: b.influencerId,
    items: (b?.items || []).map((i) => ({
      contentId: i.contentId,
      itemId: i.itemId,
      itemType: i.itemType as BundleItemType,
    })),
    isPlatformBundle: b.isPlatformBundle,
    freeTrial: getFreeTrialFromSubscriptionTier(b.subscriptionTier),
    price: getPricesFromSubscriptionTier(b.subscriptionTier),
    providerPriceId: getProviderPriceIds(b.subscriptionTier),
    pricing: b.pricing,
    status: b.status,
    description: b.description,
    pushNotificationMessage: b.pushNotificationMessage || undefined,
    pushNotificationSentAt: b.pushNotificationSentAt || undefined,
    pushNotificationUsageCount: b.pushNotificationUsageCount,
    upgradeDeeplinkUrl: b.upgradeDeeplinkUrl || undefined,
  }));
  return [
    ...mappedBundles.filter((b) => !b.isPlatformBundle),
    ...mappedBundles.filter((b) => b.isPlatformBundle),
  ];
};

export const getPricesFromSubscriptionTier = (
  subscriptionTier: RawSubscriptionTier,
): SubscriptionBundlePrice => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Month,
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Year,
  );

  return {
    monthly: monthlyPrice?.price ? monthlyPrice.price / 100 : undefined,
    annual: annualPrice?.price ? annualPrice.price / 100 : undefined,
  };
};

export const getFreeTrialFromSubscriptionTier = (
  subscriptionTier: RawSubscriptionTier,
) => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Month,
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Year,
  );

  return {
    monthly: !!monthlyPrice?.isFreeTrialEnabled,
    annual: !!annualPrice?.isFreeTrialEnabled,
  };
};

export const getProviderPriceIds = (
  subscriptionTier: RawSubscriptionTier,
): SubscriptionProviderPriceIds => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Month,
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Year,
  );

  return {
    monthly: monthlyPrice?.providerPriceId,
    annual: annualPrice?.providerPriceId,
  };
};

export type ToggleSubscriptionBundlePayload =
  | {
      bundleId: number;
      itemId: number;
      type: BundleItemType.Program | BundleItemType.Feature;
    }
  | {
      bundleId: number;
      contentId: string;
      type: BundleItemType.Content;
    };

export const mapToggledContent = (
  bundles: SubscriptionBundle[],
  payload: ToggleSubscriptionBundlePayload,
) => {
  const { bundleId, type } = payload;
  return bundles.map((b) => {
    if (b.id !== bundleId) return b;
    switch (type) {
      case BundleItemType.Feature:
      case BundleItemType.Program: {
        const hasItem = b.items.some((i) => i.itemId === payload.itemId);
        const mappedItems = hasItem
          ? b.items.filter((i) => i.itemId !== payload.itemId)
          : [...b.items, { itemId: payload.itemId, itemType: type }];
        return {
          ...b,
          items: mappedItems,
        };
      }
      case BundleItemType.Content: {
        const hasItem = b.items.some((i) => i.contentId === payload.contentId);
        const mappedItems = hasItem
          ? b.items.filter((i) => i.contentId !== payload.contentId)
          : [...b.items, { contentId: payload.contentId, itemType: type }];
        return {
          ...b,
          items: mappedItems,
        };
      }
    }
  });
};

export const mapBundlesToInput = (
  bundles: SubscriptionBundle[],
): Array<UpdateBundleInput> =>
  bundles.map((b) => ({
    id: b.id,
    items: b.items.map((i) => {
      if (i.itemType === BundleItemType.Content) {
        return {
          contentId: i.contentId,
          itemType: i.itemType,
        };
      }
      return {
        itemId: i.itemId,
        itemType: i.itemType,
      };
    }),
  }));

export const validatePlatformBundleSubset = (bundles: SubscriptionBundle[]) => {
  const platformBundleUpdate = bundles.find(
    (bundle) => bundle.isPlatformBundle,
  );
  const exclusiveBundleUpdate = bundles.find(
    (bundle) => !bundle.isPlatformBundle,
  );

  if (!platformBundleUpdate) return false;

  const isFeaturesSubset = platformBundleUpdate?.items
    .filter((i) => i.itemType === BundleItemType.Feature)
    .every((item) =>
      exclusiveBundleUpdate?.items
        .filter((i) => i.itemType === BundleItemType.Feature)
        .some((exclusiveItem) => exclusiveItem.itemId === item.itemId),
    );

  const isProgramsSubset = platformBundleUpdate?.items
    .filter((i) => i.itemType === BundleItemType.Program)
    .every((item) =>
      exclusiveBundleUpdate?.items
        .filter((i) => i.itemType === BundleItemType.Program)
        .some((exclusiveItem) => exclusiveItem.itemId === item.itemId),
    );

  const isContentSubset = platformBundleUpdate?.items
    .filter((i) => i.itemType === BundleItemType.Content)
    .every((item) =>
      exclusiveBundleUpdate?.items
        .filter((i) => i.itemType === BundleItemType.Content)
        .some((exclusiveItem) => exclusiveItem.contentId === item.contentId),
    );

  return !isFeaturesSubset || !isProgramsSubset || !isContentSubset;
};
